import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { useTherapist } from "../../../../context/useTherapist";
import { usePublishMeditation } from "../../application/useMeditations";
import { MEDITATION_LICENSES, Meditation, MeditationLicense } from "../../domain/types";

type Props = {
    meditation: Meditation;
    onDone: () => void;
};

/**
 * Publishing a meditation from the library, without opening it.
 *
 * The same two questions the editor asks — whose it is and on what terms —
 * asked in place under the meditation, as sharing a track does: a therapist
 * tidying their library publishes several in a row, and opening each one to
 * reach the bottom of the editor was the long way round.
 */
export default function MeditationPublishForm({ meditation, onDone }: Props) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    const publish = usePublishMeditation();
    // The name it was last published under, or theirs as a start.
    const [authorName, setAuthorName] = useState(
        meditation.author_name ??
            [therapist?.first_name, therapist?.last_name].filter(Boolean).join(" ")
    );
    const [license, setLicense] = useState<MeditationLicense>(meditation.license);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setError(null);
        try {
            await publish.mutateAsync({
                id: meditation.id,
                authorName: authorName.trim(),
                license,
            });
            onDone();
        } catch {
            setError(t("meditation.publishFailed"));
        }
    };

    return (
        <form
            className="flex flex-col gap-3 border-t border-border-subtle pt-3"
            onSubmit={(e) => {
                e.preventDefault();
                void submit();
            }}
        >
            <p className="text-sm text-content-secondary">{t("meditation.publish.explainer")}</p>
            <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                    {t("meditation.fields.authorName")}
                    <input
                        className="field-base field-default h-11 w-full px-3 text-sm"
                        value={authorName}
                        autoFocus
                        onChange={(e) => setAuthorName(e.target.value)}
                    />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                    {t("meditation.fields.license")}
                    <select
                        className="field-base field-default h-11 w-full appearance-none px-3 text-sm"
                        value={license}
                        onChange={(e) => setLicense(e.target.value as MeditationLicense)}
                    >
                        {MEDITATION_LICENSES.map((value) => (
                            <option key={value} value={value}>
                                {t(`meditation.licenses.${value}`)}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            {error && (
                <p role="alert" className="text-sm text-status-critical">
                    {error}
                </p>
            )}
            <span className="flex flex-wrap justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onDone}>
                    {t("common.cancel")}
                </Button>
                <Button
                    size="sm"
                    type="submit"
                    startIcon={<Globe size={15} />}
                    disabled={!authorName.trim()}
                    loading={publish.isPending}
                >
                    {t("meditation.publish.action")}
                </Button>
            </span>
        </form>
    );
}
